/**
 * F-505-FE-03: PatientLayout
 *
 * Minimal layout for exactly 3 patient-facing screens:
 * 1. /join             — QR scan / 6-digit code entry
 * 2. /session/:id/disclaimer — Consent/disclaimer screen
 * 3. /session/:id/patient  — Active session (real-time translation)
 *
 * Design principles:
 * - Warm, reassuring, generous — airport wayfinding aesthetic
 * - Base font size 18-20px for clinical room readability
 * - NO navigation bars, NO menus, NO extraneous chrome
 * - Full-screen content area, centered on axis
 * - RTL-aware via CSS logical properties throughout
 * - Large touch targets (min 56px for primary actions on this interface)
 */

import { type ReactNode } from 'react';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface PatientLayoutProps {
  children: ReactNode;
}

// ---------------------------------------------------------------------------
// Layout
// ---------------------------------------------------------------------------

export function PatientLayout({ children }: PatientLayoutProps) {
  return (
    <div
      className="
        min-h-screen
        bg-[var(--color-bg)]
        flex flex-col
        font-body
      "
      style={{ fontSize: '18px' }}
    >
      {/* Subtle top accent line — the only visual chrome */}
      <div
        className="h-1 w-full flex-none"
        style={{
          background: 'linear-gradient(90deg, var(--color-teal) 0%, var(--color-amber) 100%)',
        }}
        aria-hidden="true"
      />

      {/* Main content — full height, centered */}
      <main
        className="
          flex-1
          flex flex-col
          items-center justify-center
          w-full
          px-6 py-10
          sm:px-8 sm:py-12
        "
        role="main"
      >
        {/* Inner constrain — generous but bounded for readability */}
        <div className="w-full max-w-sm sm:max-w-md">
          {children}
        </div>
      </main>

      {/* Subtle footer — hospital branding slot, no navigation */}
      <footer
        className="
          flex-none
          flex items-center justify-center
          px-6 py-4
          border-t border-clinical-border
        "
        aria-label="Application footer"
      >
        <p className="text-sm font-body text-clinical-text-muted">
          Clinical Interpreter
        </p>
      </footer>
    </div>
  );
}
